import { PageHero, CTASection } from '../components/UI';

// Inline SVG helper
const Svg = ({ children, className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    {children}
  </svg>
);

const values = [
  {
    icon: <Svg className="w-7 h-7 text-white"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></Svg>,
    bg: "bg-gradient-to-br from-teal to-teal-light",
    title: "Results First",
    desc: "Every recommendation, document, and strategy is built around your best chance of success — not our convenience."
  },
  {
    icon: <Svg className="w-7 h-7 text-white"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></Svg>,
    bg: "bg-gradient-to-br from-blue-500 to-indigo-500",
    title: "Radical Transparency",
    desc: "We tell you what you need to hear, not what you want to hear. Honest assessments lead to better outcomes."
  },
  {
    icon: <Svg className="w-7 h-7 text-white"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></Svg>,
    bg: "bg-gradient-to-br from-purple-500 to-violet-500",
    title: "Genuine Care",
    desc: "Your goals become our goals. We celebrate your wins and stand by you through challenges — always in your corner."
  },
  {
    icon: <Svg className="w-7 h-7 text-white"><polyline points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></Svg>,
    bg: "bg-gradient-to-br from-amber-500 to-orange-500",
    title: "Expert Excellence",
    desc: "Continuous learning and professional development keep our team at the forefront of global education and immigration trends."
  },
];

const milestones = [
  { year: "2017", title: "Travizon Founded", desc: "Started as a small consultancy in Gulberg, Lahore with a single mission: honest, expert immigration guidance." },
  { year: "2019", title: "100+ Visas Milestone", desc: "Crossed 100 successful visa approvals and expanded to include skilled migration pathways." },
  { year: "2021", title: "University Partner Network", desc: "Built partnerships with 200+ universities across 35 countries for direct admission pipelines." },
  { year: "2023", title: "Digital Nomad & Value Services", desc: "Launched digital nomad visa services and full suite of value-added post-arrival support." },
  { year: "2024", title: "500+ Visas Approved", desc: "Reached 500+ visa approvals with a 98% success rate and moved to Century Tower, Gulberg-3." },
];

export default function About({ setPage }) {
  return (
    <div>
      <PageHero
        breadcrumb="About Us"
        title="Your Journey, Our Responsibility"
        subtitle="Travizon was built with a singular mission: to make international education and migration genuinely accessible, honest, and successful for every client."
      />

      {/* ── MISSION & VISION ── */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-20">

            {/* Mission */}
            <div className="relative bg-gradient-to-br from-navy to-teal rounded-3xl p-10 text-white overflow-hidden">
              <div className="absolute top-0 right-0 w-48 h-48 rounded-full bg-white/5 -translate-y-12 translate-x-12 pointer-events-none"></div>
              <div className="relative z-10">
                <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 mb-7">
                  <Svg className="w-4 h-4 text-gold-light"><polyline points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></Svg>
                  <span className="text-gold-light text-xs font-bold tracking-widest uppercase">Our Mission</span>
                </div>
                <h2 className="font-display text-3xl font-semibold mb-5 leading-snug">Empowering Every Client with Honest, Expert Guidance</h2>
                <p className="text-white/70 leading-relaxed text-sm">
                  To empower individuals with the knowledge, guidance, and support needed to make confident, informed decisions about their global education and migration journey — without misinformation, hidden costs, or false promises.
                </p>
                <div className="mt-8 flex items-center gap-2.5 text-white/50 text-sm">
                  <Svg className="w-4 h-4 text-teal-light flex-shrink-0"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></Svg>
                  Transparent. Honest. Client-first.
                </div>
              </div>
            </div>

            {/* Vision */}
            <div className="relative bg-gradient-to-br from-cream to-gray-100 rounded-3xl p-10 overflow-hidden border border-gray-200">
              <div className="absolute bottom-0 right-0 w-48 h-48 rounded-full bg-teal/5 translate-y-12 translate-x-12 pointer-events-none"></div>
              <div className="relative z-10">
                <div className="inline-flex items-center gap-2 bg-teal/10 rounded-full px-4 py-2 mb-7">
                  <Svg className="w-4 h-4 text-teal"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></Svg>
                  <span className="text-teal text-xs font-bold tracking-widest uppercase">Our Vision</span>
                </div>
                <h2 className="font-display text-3xl font-semibold mb-5 text-navy leading-snug">Pakistan's Most Trusted Name in Global Migration</h2>
                <p className="text-gray-500 leading-relaxed text-sm">
                  To be the most trusted education and migration consultancy for students and professionals from South Asia and beyond — recognized not for the number of applications we submit, but for the lives we change.
                </p>
                <div className="mt-8 flex items-center gap-2.5 text-gray-400 text-sm">
                  <Svg className="w-4 h-4 text-teal flex-shrink-0"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></Svg>
                  South Asia's leading migration partner
                </div>
              </div>
            </div>
          </div>

          {/* ── STORY ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
            <div className="h-[460px] rounded-3xl bg-gradient-to-br from-teal to-navy flex flex-col items-center justify-center text-white text-center gap-5 p-10 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 rounded-full bg-white/5 -translate-y-10 translate-x-10 pointer-events-none"></div>
              <div className="w-20 h-20 rounded-2xl bg-white/10 flex items-center justify-center relative z-10 mb-2">
                <Svg className="w-10 h-10 text-white"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></Svg>
              </div>
              <h3 className="font-display text-3xl font-semibold relative z-10">Built on Trust.<br/>Driven by Results.</h3>
              <p className="text-white/55 text-sm max-w-[260px] leading-relaxed relative z-10">Every consultant on our team has navigated the international education or immigration journey personally.</p>
            </div>
            <div>
              <span className="section-tag">Our Story</span>
              <h2 className="font-display text-4xl sm:text-5xl text-navy font-semibold mt-1 mb-6">Why Travizon Exists</h2>
              <p className="text-gray-500 leading-relaxed mb-4">We started Travizon after watching too many students fall victim to misinformation, substandard consultancies, and overpromised results. The international education industry deserved better — and so did the students depending on it.</p>
              <p className="text-gray-500 leading-relaxed mb-4">Travizon was founded to be the consultancy we all wished we had: transparent about what's possible, honest about challenges, and relentlessly supportive through every step of the journey.</p>
              <p className="text-gray-500 leading-relaxed mb-8">Today, we're proud to have helped 500+ students and professionals from Pakistan and beyond achieve their global ambitions — without the confusion, stress, or surprises.</p>
              <div className="flex flex-col gap-3.5">
                {[
                  { icon: <Svg className="w-4 h-4 text-teal flex-shrink-0 mt-0.5"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></Svg>, strong: 'Promise:', text: 'Honest counsel, meticulous documentation, and unwavering support — from your first consultation to your first day abroad.' },
                  { icon: <Svg className="w-4 h-4 text-teal flex-shrink-0 mt-0.5"><path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></Svg>, strong: 'Location:', text: 'UGF-23,24 Century Tower, Gulberg-3, Lahore — visit us in person or connect remotely.' },
                ].map(({ icon, strong, text }) => (
                  <div key={strong} className="flex gap-3 items-start">
                    {icon}
                    <p className="text-gray-600 text-sm leading-relaxed"><strong className="text-navy">{strong}</strong> {text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── TIMELINE ── */}
          <div className="mb-4">
            <div className="text-center mb-12">
              <span className="section-tag">Our Journey</span>
              <h2 className="font-display text-4xl text-navy font-semibold mt-1">7 Years of Impact</h2>
            </div>
            <div className="relative">
              <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-teal/50 via-teal/20 to-transparent -translate-x-1/2"></div>
              <div className="flex flex-col gap-8">
                {milestones.map((m, i) => (
                  <div key={m.year} className={`flex flex-col lg:flex-row gap-6 items-center ${i % 2 !== 0 ? 'lg:flex-row-reverse' : ''}`}>
                    <div className={`flex-1 ${i % 2 !== 0 ? 'lg:text-right' : ''}`}>
                      <div className={`bg-gray-50 border border-gray-100 rounded-2xl p-6 hover:border-teal/30 hover:-translate-y-0.5 transition-all inline-block w-full lg:max-w-sm ${i % 2 !== 0 ? 'lg:ml-auto' : ''}`}>
                        <div className="font-display text-3xl font-semibold text-teal mb-1">{m.year}</div>
                        <h4 className="font-body font-bold text-navy mb-2">{m.title}</h4>
                        <p className="text-gray-500 text-sm leading-relaxed">{m.desc}</p>
                      </div>
                    </div>
                    <div className="hidden lg:flex w-10 h-10 rounded-full bg-gradient-to-br from-teal to-teal-light border-4 border-white shadow-lg flex-shrink-0 items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-white"></div>
                    </div>
                    <div className="flex-1 hidden lg:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── VALUES ── */}
      <section className="py-16 bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <span className="section-tag">Core Values</span>
            <h2 className="font-display text-4xl text-navy font-semibold mt-1">What We Stand For</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map(v => (
              <div key={v.title} className="bg-white rounded-2xl p-8 text-center shadow-card hover:-translate-y-1 hover:shadow-[0_20px_60px_rgba(11,31,58,0.1)] transition-all duration-300">
                <div className={`w-14 h-14 rounded-2xl ${v.bg} flex items-center justify-center mx-auto mb-5 shadow-lg`}>{v.icon}</div>
                <h4 className="font-body font-semibold text-navy mb-3">{v.title}</h4>
                <p className="text-gray-400 text-sm leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTASection title="Ready to Work With Us?" subtitle="Book your free consultation today and discover how Travizon can help navigate your path to global success." setPage={setPage} />
    </div>
  );
}
